import { RootState } from "@/store";
import { createAsyncThunk, createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import { APIResponse, PaginatedResponse } from "@simplerealtybits/srb-types";
import { useSelector } from "react-redux";

/**
 * Maximum number of attempts before moving to dead letters.
 */
const ERROR_THRESHOLD = 100;

/**
 * Status of the queue item.
 */
export enum QueueStatus {
    Pending = "Pending",
    Processing = "Processing",
    Completed = "Completed",
    Failed = "Failed",
}

/**
 * Type of the queue item.
 */
export enum QueueItemType {
    CreateKnock = "create-knock",
    KnockSale = "knock-sale",
}

export interface QueueItem {
    id: string;
    type: QueueItemType;
    payload: unknown;
    status: QueueStatus;
    response?: APIResponse<unknown> | PaginatedResponse<unknown>;
    error?: string;
    errorCount: number;
}

export interface QueueState {
    queue: QueueItem[];
    history: QueueItem[];
    deadLetters: QueueItem[];
}

export interface AddPayload {
    type: QueueItemType;
    payload: unknown;
    execute: (payload?: unknown) => Promise<APIResponse<unknown> | PaginatedResponse<unknown>>;
}

export type QueueItemExecutors = Partial<
    Record<
        QueueItemType,
        (payload: unknown) => Promise<APIResponse<unknown> | PaginatedResponse<unknown>>
    >
>;

export type QueueItemProcessor = {
    item: QueueItem;
    executors: QueueItemExecutors;
};

export const processQueueItem = createAsyncThunk<
    QueueItem,
    QueueItemProcessor,
    { rejectValue: { id: string; error: string } }
>("queue/processQueueItem", async (processor, { rejectWithValue }) => {
    const { item } = processor;
    try {
        const res = await processor.executors[item.type]?.(item.payload);
        if (!res || !res.success) throw new Error(res?.message || "Unknown error");
        return { ...item, status: QueueStatus.Completed, response: res };
    } catch (e) {
        return rejectWithValue({ id: item.id, error: (e as Error).message });
    }
});

const initialState: QueueState = { queue: [], history: [], deadLetters: [] };

export const queueSlice = createSlice({
    name: "queue",
    initialState,
    reducers: {
        addToQueue: (state, { payload }: PayloadAction<AddPayload>) => {
            const newItem: QueueItem = {
                ...payload,
                id: crypto.randomUUID(),
                status: QueueStatus.Pending,
                errorCount: 0,
            };
            console.log("Adding to queue", newItem);
            state.queue.push(newItem);
        },
        removeFromQueue: (state, { payload }: PayloadAction<{ id: string }>) => {
            state.queue = state.queue.filter((i) => i.id !== payload.id);
        },
        updateQueueItem: (
            state,
            { payload }: PayloadAction<Partial<QueueItem> & { id: string }>
        ) => {
            const idx = state.queue.findIndex((i) => i.id === payload.id);
            if (idx !== -1) state.queue[idx] = { ...state.queue[idx], ...payload };
        },
        clearQueue: (state) => void (state.queue = []),
        clearHistory: (state) => void (state.history = []),
    },
    extraReducers: (builder) => {
        builder.addCase(processQueueItem.pending, (state, { meta: { arg } }) => {
            const idx = state.queue.findIndex((q) => q.id === arg.item.id);
            if (idx === -1) return;

            const item = state.queue[idx] as Draft<QueueItem>;
            item.status = QueueStatus.Processing;
        });

        builder.addCase(processQueueItem.fulfilled, (state, { payload }) => {
            const idx = state.queue.findIndex((q) => q.id === payload.id);
            if (idx === -1) return;

            state.queue.splice(idx, 1);
            state.history.push(payload);
        });

        builder.addCase(processQueueItem.rejected, (state, { payload }) => {
            if (!payload) return;
            const idx = state.queue.findIndex((q) => q.id === payload.id);
            if (idx === -1) return;

            const item = state.queue[idx] as Draft<QueueItem>;
            item.error = payload.error;
            item.errorCount += 1;

            if (item.errorCount >= ERROR_THRESHOLD) {
                item.status = QueueStatus.Failed;
                state.queue.splice(idx, 1);
                state.deadLetters.push(item);
            } else {
                item.status = QueueStatus.Failed;
            }
        });
    },
});

export const { addToQueue, removeFromQueue, updateQueueItem, clearQueue, clearHistory } =
    queueSlice.actions;
export const queueReducer = queueSlice.reducer;
export const useQueueData = () => useSelector((state: RootState) => state.queue);
